/** Fixed logic timestep: 10 ticks per second (tech-design §2.3). */
export const TICK_MS = 100;

/**
 * Pure accumulator drain: given accumulated real milliseconds and a fixed
 * timestep, return how many whole logic ticks are due and the leftover. Keeping
 * this pure means the background-catch-up and offline paths share one tested
 * implementation (tech-design §2.3–§2.4).
 */
export function drainAccumulator(
  accumulatedMs: number,
  tickMs: number = TICK_MS,
): { ticks: number; remainderMs: number } {
  if (tickMs <= 0) {
    throw new RangeError(`tickMs must be positive, got ${String(tickMs)}`);
  }
  if (accumulatedMs < 0) {
    return { ticks: 0, remainderMs: 0 };
  }
  const ticks = Math.floor(accumulatedMs / tickMs);
  return { ticks, remainderMs: accumulatedMs - ticks * tickMs };
}

export interface LoopHandle {
  stop(): void;
}

/**
 * Drive `onTick(dtTicks)` on a fixed-timestep accumulator over
 * `requestAnimationFrame`. When the tab is backgrounded, rAF throttles and the
 * accumulator catches up in a batch on the next frame — the same code path as
 * offline progress, no special case.
 *
 * `onFrame(dtSeconds)` fires every animation frame with the real elapsed time,
 * for framerate-independent display work (the number interpolator) that must
 * stay smooth at 60 fps while logic ticks at 10 tps (tech-design §4).
 */
export function startLoop(
  onTick: (dtTicks: number) => void,
  onFrame?: (dtSeconds: number) => void,
): LoopHandle {
  let last = performance.now();
  let accumulated = 0;
  let frame = 0;
  let running = true;

  const step = (nowMs: number): void => {
    if (!running) {
      return;
    }
    const elapsedMs = nowMs - last;
    accumulated += elapsedMs;
    last = nowMs;
    const { ticks, remainderMs } = drainAccumulator(accumulated);
    accumulated = remainderMs;
    if (ticks > 0) {
      onTick(ticks);
    }
    onFrame?.(elapsedMs / 1000);
    frame = requestAnimationFrame(step);
  };

  frame = requestAnimationFrame(step);

  return {
    stop(): void {
      running = false;
      cancelAnimationFrame(frame);
    },
  };
}
